<template>
  <div class="m-4 md:mx-8 md:mt-16">
    <ButtonAction
      v-if="fromProduction"
      @action="redirectToPreviousRoute()"
      class="mb-4"
      color="gray"
    >
      {{ $t("lang.msg.goBack") }}
    </ButtonAction>
    <div class="grid grid-cols-1 lg:grid-cols-detail gap-8">
      <section class="w-full rounded-t">
        <div class="px-4 text-lg font-semibold bg-secundario text-primario tracking-wide rounded-t">
          <h2>{{ $t("lang.msg.certificates") }}</h2>
        </div>
        <form class="bg-white p-4 rounded-b space-y-4">
          <div class="flex justify-between">
            <label class="inline-flex items-center">
              <input
                v-model="selectedOption"
                type="radio"
                class="form-radio"
                value="factory"
                @change="changeCombo(true)"
              >
              <span class="ml-2 cursor-pointer">{{ $t("lang.msg.factory") }}</span>
            </label>
            <label class="inline-flex items-center ml-6">
              <input
                v-model="selectedOption"
                type="radio"
                class="form-radio"
                value="substructure"
                @change="changeCombo(false)"
              >
              <span class="ml-2 cursor-pointer">{{ $t("lang.msg.substructure") }}</span>
            </label>
          </div>
          <InputWrapper
            v-if="selectedOption == 'factory'"
            :label="$t('lang.msg.factory')"
            content="input"
            block
          >
            <Select
              id="idFactory"
              :data="factories"
              track-by="factoryName"
              v-model.number="newItem.idFactory"
            />
          </InputWrapper>
          <InputWrapper
            v-else
            :label="$t('lang.msg.substructure')"
            content="input"
            block
          >
            <Select
              id="idSubStructure"
              :data="substructures"
              track-by="subName"
              v-model.number="newItem.idSubStructure"
            />
          </InputWrapper>
          <div class="flex space-x-2">
            <InputWrapper :label="$t('lang.msg.dischargeDate')" content="input" block>
              <v-date-picker
                v-model="newItem.certificateDate"
                mode="single"
                title-position="left"
                :input-props="{
                  class: 'datepicker hover:border-blue-500',
                  placeholder: $t('lang.msg.dischargeDate'),
                }"
              />
            </InputWrapper>
            <InputWrapper :label="$t('lang.msg.dateOfExpiry')" content="input" block>
              <v-date-picker
                v-model="newItem.certificateEndDate"
                mode="single"
                title-position="left"
                :input-props="{
                  class: 'datepicker hover:border-blue-500',
                  placeholder: $t('lang.msg.dateOfExpiry'),
                }"
              />
            </InputWrapper>
          </div>
          <InputWrapper :label="$t('lang.fields.name')" content="input" block>
            <InputText v-model="newItem.certificateName" :name="$t('lang.fields.name')" />
          </InputWrapper>
          
          
          <AdminToggleButton v-model="newItem.certificateActive" />

          <span class="flex w-full rounded-md shadow-sm sm:w-auto">
            <button
              type="button"
              class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-primario hover:bg-primario text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
              @click="addOrEditItem(`api/Certificats/Add`, `api/Certificats/Update`)"
            >{{ newItem.idCertificate == undefined ? $t('lang.msg.add') : $t('lang.msg.update') }}</button>
          </span>
        </form>
      </section>

      <!-- === DOCUMENTOS === -->
      <div class="flex flex-col w-full" v-if="newItem.idCertificate">
        <section>
          <div class="px-4 text-lg font-semibold bg-secundario text-primario tracking-wide rounded-t">
            <h2>{{ $t("lang.msg.documents") }}</h2>
          </div>
          <div class="bg-white">
            <vue-dropzone
              ref="dropzone"
              id="dropzone"
              :options="dropzoneOptions"
              @vdropzone-sending="sendingEvent"
            />

            <div class="gallery">
              <ImageGallery
                v-for="(image, imageIndex) in images"
                :key="`image_${imageIndex}`"
                :url="image.url"
                :id-foto="image.idFoto"
                @image-deleted="imageDeleted"
              />
            </div>
          </div>
        </section>
      </div>

      <InputWrapper
        v-if="newItem.idCertificate"
        :label="$t('lang.msg.dateRange')"
        content="input"
        block
      >
        <Select
          v-model.number="selectedInterval"
          id="idCertificationDay"
          :data="dateIntervals"
          track-by="fechaAlta"
          track-by2="fechaCaducidad"
          placeholder="Elija un rango de fechas para filtrar documentos y producciones"
          separator=" / "
          @change="changeDateInterval"
        />
      </InputWrapper>
        
      <!-- =============== -->
      <!-- === PRENDAS === -->
      <!-- =============== -->
      <section class="col-span-2">
        <div class="px-4 text-lg font-semibold bg-secundario text-primario tracking-wide rounded-t">
          <h2>{{ $t("lang.msg.associatedProductions") }}</h2>
        </div>
        <div class="bg-white p-4 rounded-b">
          <!-- === PLANNING TABLE === -->
          <ul id="PlanningTable" class="space-y-6">
            <li
              v-for="(item, index) in planning"
              :key="`item_${index}`"
              class="xl:pr-2 grid items-center grid-cols-1 sm:grid-cols-2 xl:grid-cols-production gap-0 sm:gap-4"
              :class="index % 2 == 0 ? 'bg-gray-200' : ''"
            >
              <!-- ======================= -->
              <!-- === PLANNING IMAGES === -->
              <!-- ======================= -->
              <PlanningImages
                :id-comercial-request="item.idComercialRequest"
                :join-life="item.joinLife"
                :reoperate="item.productionReoperation"
                :official-delivery-date1="item.officialDeliveryDate1"
                :reference-number="item.comercialRequestReferenceNumber"
              />
              <!-- ====================================-->
              <!-- === PLANNING PRODUCTION PROGRESS === -->
              <!-- ==================================== -->
              <PlanningProductionProgress :production="item" />
              <div class="xl:space-y-2 xl:my-2 cursor-pointer" @click="viewPlanning(index)">
                <PlanningTableItem :index="index" :data="concatTwoValues(item.comercialRequestReference, item.comercialRequestReferenceNumber)" :label="$t('lang.msg.internalReference')" />
                <PlanningTableItem :index="index" :data="item.comercialRequestClientReference" :label="$t('lang.msg.clientReference')" />
                <PlanningTableItem :index="index" :data="item.groupName" :label="$t('lang.msg.group')" />
                <PlanningTableItem :index="index" :data="item.clientName" :label="$t('lang.msg.client')" />
              </div>
              <div class="xl:space-y-2 xl:my-2 cursor-pointer" @click="viewPlanning(index)">
                <PlanningTableItem :index="index" :data="item.sectionName" :label="$t('lang.msg.department')" />
                <PlanningTableItem :index="index" :data="item.totalQuantity" :label="$t('lang.msg.units')" />
                <PlanningTableItem :index="index" :data="item.factoryName" :label="$t('lang.msg.factory')" />
                <PlanningTableItem :index="index" :data="item.officialDeliveryDate2|moment" :label="$t('lang.msg.officialDate1')" />
              </div>
              <div class="xl:my-2 cursor-pointer">
                <div class="flex justify-end items-center xl:flex-col space-x-4 xl:space-x-0 xl:space-y-3">
                  <ButtonRounded
                    @action="viewPlanning(index)"
                    title="Ver Producción"
                    color="blue"
                    large
                  >
                    <svg class="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.5 283.5">
                      <path d="M275.2 50.3c-3.5-4.5-7.7-8.3-12.7-11.2-7.3-4.4-15.1-7.5-23.7-8.3-5.1-.5-10.2-.5-15.2.8-4.8 1.3-9 3.5-12.6 7-3.3 3.2-4.8 7.1-5.4 11.6-.1.9-.2 1.9-.4 2.8-.2 1.3-.5 1.6-1.9 1.7-2 .1-4 .1-6.1-.1-4.7-.3-9.3-.8-14-.7-1.6 0-3.2.1-4.7-.1-4.7-.5-9.3-.4-14-.6-5.8-.2-11.6-.1-17.4-.1-7.4 0-14.8-.1-22.1.2-5.7.2-11.5.7-17.2.5-.9 0-1.8.1-2.6.1-4.1.3-8.2.7-12.3.6-.9 0-1.9 0-2.8.1-4.5.4-8.9.7-13.4.7-.8 0-1.5 0-2.3.1-4.9.6-9.9.7-14.9 1.3-4 .5-8 1-11.9 1.9-5.5 1.2-10.8 3-15.9 5.3-9.1 4.1-16.1 10.5-20.9 19.3-4.7 8.8-6.4 18.4-6.5 28.3 0 1.4.3 2.9.3 4.3 0 2.7.5 5.4 1 8.1 1.3 6.6 3.5 13 6.5 19.1 3 6.1 6.5 11.8 10.7 17.1 3.6 4.5 7.5 8.8 11.9 12.7 7.2 6.4 15.1 11.6 23.9 15.4 6 2.6 12.2 4.8 18.6 6.2 3 .6 6 1.3 9.1 1.7 4.7.7 9.5.9 14.3 1.2 4.7.3 9.3-.1 14-.5 4.6-.3 9.3-1.1 13.8-2 5.7-1.1 11.2-2.6 16.6-4.6 10.8-4.1 20.5-10.1 28.3-18.7 4.2-4.6 7.4-9.8 8.7-16.1 1.2-5.9.5-11.5-2.1-16.9-1.9-4-4.5-7.6-7.9-10.6-2.8-2.4-5.9-4.3-9.3-5.7-7.8-3.2-16-3.4-24.2-2.8-5.9.4-11.7 2-17.2 4.1-9.1 3.5-17.3 8.6-23.9 15.9-3.1 3.5-5.6 7.3-7.2 11.7-.8 2.1-1.3 4.3-1.6 6.6-.2 1.8-1.2 2.8-2.9 3.3-2.6.7-5.1 1.3-7.8 1.6-6.5.8-12.6-.5-18.4-3.1-6-2.6-11-6.5-14.8-11.7-4.6-6.2-7.3-13.1-7.3-20.9v-4.2c0-6 1.8-11.4 4.9-16.5 4.3-6.8 10.6-11.1 17.8-14.2 5.2-2.3 10.7-3.7 16.3-4.8 5-1 10-1.5 15-2.1 2.6-.3 5.3-.3 7.9-.3 5.4.1 10.7-.1 16.1.3 5 .3 10.1-.1 15.1-.2 7.7-.1 15.4-.4 23.1-.7 8.3-.3 16.7-.2 25 .4 2.3.2 4.7.1 7 .4 5.1.6 10.1 1.2 15.1 2.5 4.3 1.1 8.6 2.4 12.6 4.6 5.3 2.9 9.1 7.1 11.4 12.7 2 4.9 3.1 10.1 3.8 15.4.7 5.4 1 10.8 1 16.2 0 4-.5 7.9-.4 11.9 0 .3 0 .5-.1.8-.7 5.2-1 10.5-2 15.7-.6 2.9-1.2 5.8-2.1 8.6-1.5 4.8-3.5 9.3-6 13.7-2.3 4-5.1 7.7-8.2 11.1-7.2 7.7-16.1 12.9-25.9 16.5-2.8 1.1-5.7 1.9-8.7 2.7-3.9 1.1-7.8 1.9-11.8 2.7-4 .7-8.1 1.2-12.1 1.8-4.5.6-9.1.7-13.6.9-7.1.3-14.2-.2-21.3-.9-4.5-.5-8.9-1.1-13.3-1.9-6.6-1.2-13-2.9-19.4-5-9-2.9-17.6-6.6-25.9-11-5.9-3.2-11.6-6.8-17.1-10.8-4.7-3.5-9.8-6.5-15.5-8-4-1.1-7.3 0-10.6 2.1-.9.6-1.8 1.4-2.5 2.3-2.8 3.6-2.8 7.6-2 11.7 1 5.1 3.5 9.6 6.6 13.7 3.3 4.3 7.5 7.5 11.8 10.5 5.3 3.7 10.9 6.9 16.7 9.6 6.5 3 13.2 5.7 20.1 7.9 6.6 2.1 13.2 3.7 19.9 5.2 2 .4 3.9.8 5.9 1.2 2.7.4 5.4.4 8 1.3.5.2 1 .2 1.5.2 3.2.1 6.3.6 9.4 1 6.2.7 12.3.7 18.5.9 4.1.1 8.2.1 12.3-.3 3.2-.3 6.4.1 9.6-.5 2.3-.4 4.7-.1 7-.6 2.9-.6 6-.2 8.7-1.5.2-.1.4-.1.6-.1 2.7-.2 5.4-.6 8-1.2 7-1.6 14-3.3 20.8-5.5 8.6-2.8 16.8-6.6 24.6-11.1 8.1-4.6 15.4-10.3 21.7-17.2 2.7-3 5.3-6.1 7.5-9.5 3.4-5.2 6-10.9 7.8-17 2.3-8.2 3.5-16.5 3.9-25 .5-10.8-.2-21.6-1.6-32.3-.6-4.1-1-8.3-2-12.3l-.9-4.5c-.8-4.2-1.7-8.4-2.8-12.5-.7-2.6-1.5-5.2-2.2-7.8-.4-1.5 0-2 1.4-2.3 1-.3 2.1-.5 3.1-.8 7.4-1.9 13.6-5.8 18.4-11.9 2.3-2.9 4.1-6.2 4.7-9.8 1.4-6.9-.5-13.2-4.8-18.8zM254.8 56c-1.7 6.6-10.1 7-14.7 4 0 0-2.5-10.2.7-12.8 3.2-2.6 7.4-1.1 7.4-1.1s8.2 3.3 6.6 9.9z" />
                    </svg>
                  </ButtonRounded>
                  <ButtonRounded
                    v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                    @action="openDiscontinueModal(index, item.idProduction)"
                    title="Descatalogar producción"
                    color="red"
                    large
                  >
                    <svg class="w-6 h-6 fill-current" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        d="M6 18L18 6M6 6L18 18"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </ButtonRounded>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <TheModal :open="isModalOpen" title="Descatalogar Muestra" @close="isModalOpen = false">
          <template #body>
            <p class="mt-4 pr-16 text-gray-700 font-semibold">
              {{ $t('lang.msg.discontinueSample') }}
            </p>
            <textarea
              rows="2"
              class="form-textarea w-full mt-2"
              placeholder="Escriba el motivo aquí"
              v-model="current.cancellationReason"
            />
          </template>
          <template #action>
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button
                type="button"
                class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 hover:bg-red-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
                @click="discontinuePlanning"
                @keyup.enter="discontinuePlanning"
              >{{ $t('lang.msg.discontinue') }}</button>
            </span>
          </template>
        </TheModal>
      </section>
    </div>
  </div>
</template>

<script>
// MIXINS
import AdminMixin from "@/components/mixins/AdminMixin";
import CalendarMixin from "@/components/mixins/CalendarMixin";

import PlanningTableItem from "@/components/shared/planning/PlanningTableItem";
import TheModal from "@/components/shared/TheModal";

// DROPZONE
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

export default {
  name: "CertificationDetail",
  mixins: [AdminMixin, CalendarMixin],
  components: {
    vueDropzone: vue2Dropzone,
    PlanningTableItem,
    TheModal
  },
  data() {
    return {
      params: {},
      idCertificate: 0,
      selectedOption: 'factory',
      selectedInterval: null,
      dateIntervals: [],
      factories: [],
      substructures: [],
      images: [],
      planning: [],
      current: {},
      dropzoneOptions: {
        timeout: 120000,
        maxFilesize: 2048,
        url: `${this.$API_URL}/api/DocumentCertificats/Add`,
        thumbnailWidth: 175,
        thumbnailHeight: 175,
        paramName: "files"
      }
    }
  },
  computed: {
    fromProduction() {
      return this.$route.params.fromProduction;
    }
  },
  created() {
    if (this.$route.params.idCertificate) {
      this.idCertificate = parseInt(this.$route.params.idCertificate)
      this.isEditing = true;

      // Fetch api calls
      this.params = { idCertificate: this.idCertificate };
      this.fetchOneCertificate();
    }
    this.fetchComboFabricas();
    this.fetchComboSubstructures();
  },
  methods: {
    fetchOneCertificate() {
      this.$axios.post("api/Certificats/PostCertificats", this.params).then((response) => {
        this.newItem = response.data[0];

        // Recogemos los documentos y producciones en función de la fecha de alta y caducidad.
        this.filterDocumentsByDates(this.newItem);
        this.fetchClothes(this.newItem);

        // Formateamos las fechas del certificado a Date.
        this.formatDatetimesToDates(this.newItem, [
          "certificateDate",
          "certificateEndDate"
        ]);

        // Recogemos el histórico de fechas del certificado.
        this.fetchDateIntervals();

        // Preselect if the certificate has a factory or substructure.
        this.selectedOption = this.newItem.idFactory != null ? 'factory' : 'substructure';
      });
    },
    fetchDateIntervals() {
      this.$axios.post("api/Certificats/PostHistoryCertificate", { 
        idCertificate: this.newItem.idCertificate
      })
      .then( response => {
        let data = response.data;

        data.forEach(element => {
          element.certificateDate = element.fechaAlta,
          element.certificateEndDate = element.fechaCaducidad,
          element.fechaAlta = this.$moment(element.fechaAlta, "YYYY-MM-DDTHH:mm:ss").format('DD-MM-YYYY'),
          element.fechaCaducidad = this.$moment(element.fechaCaducidad, "YYYY-MM-DDTHH:mm:ss").format('DD-MM-YYYY')
        });

        // Just make sure we can get at least 1 item.
        if (data.length > 0) {
          this.selectedInterval = data.slice(-1)[0].idCertificationDay;
        }

        this.dateIntervals = data;
      });
    },
    /**
     * Reacts to the dates dropdown to filter a certain certification dates.
     */
    changeDateInterval(idCertificationDay) {
      let certificationDates = this.dateIntervals.find(element => {
        return element.idCertificationDay == idCertificationDay
      });

      this.filterDocumentsByDates(certificationDates);
    },
    /**
     * Takes a Certification object to filter the documents between the certification dates.
     * @params Certification: certification
     */
    filterDocumentsByDates(certification) {
      // Clone the object to format the dates afterwards
      let data = { ...certification };

      this.$axios.post(`api/Certificats/GetThisDocument`, {
        idCertificate: data.idCertificate,
        certificateDate: data.certificateDate,
        certificateEndDate: data.certificateEndDate
      }).then( response => {
        this.images = response.data;
      });
    },
    /**
     * Takes a Certification object to filter the productions between the certification dates.
     * @params Certification: certification
     */
    fetchClothes(certification) {
      // Clone the object to format the dates afterwards
      let data = { ...certification };

      this.$axios.post("api/Certificats/GetThisCertificatSample", {
        idCertificat: data.idCertificate,
        certificateDate: data.certificateDate,
        certificateEndDate: data.certificateEndDate
      }).then( response => {
        this.planning = response.data;
      });
    },
    fetchComboFabricas() {
      this.$axios.get("api/DocumentCertificats/ComboFactory").then((response) => {
        this.factories = response.data;
      });
    },
    fetchComboSubstructures() {
      this.$axios.get("api/DocumentCertificats/ComboSubStructure").then((response) => {
        this.substructures = response.data;
      });
    },
    changeCombo(isFactory) {
      if (isFactory) {
        this.$set(this.newItem, 'idSubStructure', 0);
      } else {
        this.$set(this.newItem, 'idFactory', 0);
      }
    },
    addOrEditItem(addURL, editUrl ) {
      let url = "";
      let message = "";

      // Different URL and MESSAGE if we are storing or updating.
      if (this.isEditing) {
        message = "Certificación actualizada";
        url = editUrl;
      } else {
        message = "Certificación añadida";
        url = addURL;
      }

      // Copy the certification object so we dont have to format the dates back.
      let newItemCopy = { ...this.newItem };

      // Format both dates to datetime.
      this.formatDatesToDatetimes(newItemCopy, [
        "certificateDate",
        "certificateEndDate"
      ]);

      this.$axios.post(url, newItemCopy).then(() => {
        this.openAlert('success', message, null);
        if (!this.$route.params.idCertificate) {
          this.$router.push({ name: "adminCertificates" });
        }
      });
    },
    sendingEvent(file, xhr, formData) {
      let clone = {...this.newItem};
      this.formatDatesToDatetimes(clone, ["certificateDate"]);

      formData.append("idCertificate", this.newItem.idCertificate);
      formData.append("documentDate", clone.certificateDate);
    },
    // Planning production functions
    viewPlanning(index) {
      this.$router.push({
        name: "production",
        params: {
          idProduction: this.planning[index].idProduction
        }
      });
    },
    openDiscontinueModal(index, idProduction) {
      this.isModalOpen = true;
      this.current.index = index;
      this.current.idProduction = idProduction;
    },
    discontinuePlanning() {
      this.$axios
        .post("api/Productions/Descatalogo", {
          idUser: this.user.id,
          idProduction: this.current.idProduction,
          discontinuedProductionReason: this.current.cancellationReason
        })
        .then(() => {
          this.planning.splice(this.current.index, 1);
          this.current = {};
          this.isModalOpen = false;
          this.openAlert("success", "Producción descatalogada", null);
        })
        .catch(() => this.showFriendlyError());
    },
    // Redirect to previous route
    redirectToPreviousRoute() {
      this.$router.back();
    },
    imageDeleted(idFoto) {
      let images = this.images.filter(image => {
        return image.idFoto != idFoto
      });

      this.images = images;
      this.$forceUpdate();
    }
  }
}
</script>